import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: signInError } = await signIn(email, password);
    setLoading(false);
    if (signInError) {
      setError(signInError);
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div className="flex min-h-screen bg-cream dark:bg-midnight-deep">
      {/* Left brand panel — CodeLab Mission Control */}
      <div
        className="hidden lg:flex lg:w-1/2 items-center justify-center relative overflow-hidden"
        style={{ background: 'linear-gradient(to bottom, #0F172A 0%, #0A0A0F 100%)' }}
      >
        {/* Dot-grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />
        {/* by CodeLab label top-left */}
        <div className="absolute top-6 left-8 inline-flex items-center gap-1.5">
          <i className="ri-terminal-box-line text-electric text-sm" />
          <span className="text-[11px] font-medium text-electric">by CodeLab</span>
        </div>

        <div className="relative z-10 text-center px-12">
          <img
            src="/icon-192.png"
            alt="Zman logo"
            className="w-[72px] h-[72px] mx-auto mb-6 rounded-2xl"
            style={{ boxShadow: '0 0 30px rgba(212,168,67,0.3)' }}
          />
          <h1 className="font-display text-[42px] font-bold text-white mb-1 tracking-tight">Zman</h1>
          <p className="text-base italic text-gold mb-6">Your Time. Your Habits.</p>

          <div className="w-10 h-px bg-gold mx-auto mb-6" />

          <ul className="inline-flex flex-col gap-3.5 text-left">
            {[
              'Hebrew calendar & Zmanim',
              'Automated daily phone reminders',
              'Streak tracking',
              'Shabbat & holiday aware',
            ].map((feature) => (
              <li key={feature} className="flex items-center gap-2.5 text-white text-sm">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                  <i className="ri-check-line text-gold text-xs" />
                </span>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Powered by CodeLab bottom-left */}
        <div className="absolute bottom-6 left-8 inline-flex items-center gap-1.5 text-[11px] text-slate-500">
          <i className="ri-terminal-box-line text-electric text-xs" />
          Powered by CodeLab
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 relative overflow-hidden">
        {/* Faint decorative geometric pattern bottom-right */}
        <div
          className="absolute bottom-[-40px] right-[-40px] w-56 h-56 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(60deg, #D4A843 0 1px, transparent 1px 16px), repeating-linear-gradient(-60deg, #D4A843 0 1px, transparent 1px 16px)',
          }}
        />
        <div className="w-full max-w-[380px] relative z-10">
          <div className="lg:hidden text-center mb-8">
            <img src="/icon-192.png" alt="Zman logo" className="w-14 h-14 mx-auto mb-4 rounded-xl" />
            <h1 className="font-display text-2xl font-bold text-foreground-950">Zman</h1>
            <p className="text-sm text-gold">Your Time. Your Habits.</p>
          </div>

          <img src="/icon-192.png" alt="Zman logo" className="hidden lg:block w-8 h-8 rounded-lg mb-6 opacity-90" />
          <h2 className="font-display text-[28px] font-bold text-foreground-950 mb-1">Welcome back</h2>
          <p className="text-sm text-foreground-500 mb-8">Sign in to your accountability dashboard</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-[13px] font-medium text-foreground-600 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                dir="ltr"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 text-sm rounded-xl border border-background-300 bg-surface dark:bg-midnight-card text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-[13px] font-medium text-foreground-600">
                  Password
                </label>
                <Link to="/forgot-password" className="text-[13px] text-electric hover:underline font-medium transition">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  dir="ltr"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 pr-11 text-sm rounded-xl border border-background-300 bg-surface dark:bg-midnight-card text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition cursor-pointer"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-12 flex items-center justify-center text-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-midnight/40 border-t-midnight rounded-full animate-spin dark:border-white/40 dark:border-t-white" />
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-foreground-500">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="text-electric hover:underline font-medium transition">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}